import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Eye, Pencil } from "lucide-react";
import { EmailPreview } from "@/components/EmailPreview";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fill = (s, name) => s.replace(/\{(nombre|name)\}/g, name || "");

export const DocEmailDialog = ({ open, onOpenChange, tid, charge, payload, onSent, resend = false, filename }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState({ to: "", cc: "", subject: "", message: "", from_name: "" });
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const load = () => api.get("/docs/email-defaults").then((r) => {
    const d = r.data[tid];
    setTpl(d);
    setF({ to: charge?.email || "", cc: "", subject: d.subject, message: isAdmin ? d.message : fill(d.message, charge?.guest_name), from_name: d.from_name });
  }).catch((e) => toast.error(apiError(e).message));
  useEffect(() => { if (open && tid) { setShowMail(false); load(); } }, [open, tid, charge]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const send = async () => {
    setBusy(true);
    try {
      const { from_name, ...mail } = f;
      await api.post(payload.url, { ...payload.body, ...mail, ...(isAdmin ? { from_name } : {}), message: fill(f.message, charge?.guest_name), cc: f.cc.trim() || null });
      toast.success(`Documento ${resend ? "reenviado" : "enviado"} a ${f.to}${f.cc.trim() ? ` y ${f.cc.trim()}` : ""}`);
      onOpenChange(false); onSent();
    } catch (e) { toast.error(apiError(e).message); } finally { setBusy(false); }
  };
  const saveDefault = async () => {
    try { await api.put(`/docs/email-defaults/${tid}`, { subject: f.subject, message: f.message, from_name: f.from_name }); toast.success("Remitente y mensaje predeterminados guardados para esta plantilla"); setTpl((t) => ({ ...t, custom: true, from_name: f.from_name.trim() })); }
    catch (e) { toast.error(apiError(e).message); }
  };
  const resetDefault = async () => {
    try { await api.delete(`/docs/email-defaults/${tid}`); toast.success("Se restauraron el remitente y el mensaje originales"); load(); }
    catch (e) { toast.error(apiError(e).message); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="doc-email-dialog">
        <DialogHeader>
          <DialogTitle>{resend ? "Reenviar documento guardado" : "Enviar documento por correo"}</DialogTitle>
          <DialogDescription>{resend ? "Se reenvía el mismo PDF del historial, sin volver a generarlo." : "Se envía el PDF adjunto y queda guardado en el historial del cobro."}</DialogDescription>
        </DialogHeader>
        {showMail ? <EmailPreview tid={tid} fromName={isAdmin ? f.from_name : null} to={f.to} cc={f.cc} subject={f.subject} message={fill(f.message, charge?.guest_name)} filename={filename} /> : (
        <div className="space-y-3">
          <div className="space-y-1.5"><Label htmlFor="doc-email-to">Para<span className="ml-0.5 text-rose-500">*</span></Label>
            <Input id="doc-email-to" type="email" value={f.to} onChange={set("to")} placeholder="correo@huesped.com" data-testid="doc-email-to" /></div>
          <div className="space-y-1.5"><Label htmlFor="doc-email-cc">Copia <span className="text-slate-400">(opcional)</span></Label>
            <Input id="doc-email-cc" type="email" value={f.cc} onChange={set("cc")} placeholder="otro@correo.com" data-testid="doc-email-cc" /></div>
          <div className="space-y-1.5"><Label htmlFor="doc-email-subject">Asunto<span className="ml-0.5 text-rose-500">*</span></Label>
            <Input id="doc-email-subject" value={f.subject} onChange={set("subject")} data-testid="doc-email-subject" /></div>
          <div className="space-y-1.5"><Label htmlFor="doc-email-message">Mensaje<span className="ml-0.5 text-rose-500">*</span></Label>
            <Textarea id="doc-email-message" rows={7} value={f.message} onChange={set("message")} data-testid="doc-email-message" />
            {isAdmin && <p className="text-xs text-slate-500" data-testid="doc-email-placeholder-hint">{"{nombre}"} se cambia por el nombre del huésped al enviar.</p>}</div>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/50 p-3" data-testid="doc-email-defaults-box">
              <div className="w-full space-y-1.5"><Label htmlFor="doc-email-from" className="text-xs text-violet-900">Nombre del remitente</Label>
                <Input id="doc-email-from" value={f.from_name} onChange={set("from_name")} maxLength={80} className="bg-white" data-testid="doc-email-from-name" /></div>
              <p className="mr-auto text-xs text-violet-800">Remitente y mensaje predeterminados de esta plantilla{tpl?.custom ? " · personalizado" : ""}</p>
              {tpl?.custom && <Button type="button" variant="ghost" size="sm" onClick={resetDefault} data-testid="doc-email-reset-default-btn"><RotateCcw size={14} className="mr-1.5" />Original</Button>}
              <Button type="button" variant="outline" size="sm" onClick={saveDefault} disabled={f.subject.trim().length < 3 || f.message.trim().length < 3 || (f.from_name || "").trim().length < 2} data-testid="doc-email-save-default-btn"><Save size={14} className="mr-1.5" />Guardar como predeterminado</Button>
            </div>
          )}
        </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="doc-email-cancel-btn">Volver</Button>
          <Button variant="outline" onClick={() => setShowMail((s) => !s)} data-testid="doc-email-preview-toggle">
            {showMail ? <><Pencil size={15} className="mr-1.5" />Editar correo</> : <><Eye size={15} className="mr-1.5" />Ver correo</>}
          </Button>
          <Button onClick={send} disabled={busy || !f.to.trim() || f.subject.trim().length < 3 || f.message.trim().length < 3} className="bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid="doc-email-send-btn">
            {busy ? "Enviando…" : resend ? "Reenviar" : "Enviar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
